import { useEffect } from "react";
import { useIntl } from "react-intl";

import { useZebraMachine } from "@/hooks/machines/zebra";
import { type ErrorsAndWarningsProps, type MachineFaultDisplay } from "@/schemas";

function createDisplayItem(machineId: string, code: number, intl: ReturnType<typeof useIntl>): MachineFaultDisplay {
	const translationKey = `ZebraPrinter Error ${code}`;

	return {
		machineId,
		title: intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey }),
		image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/zebraprinter/error/${code}.svg`,
		fallbackImage: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/zebraprinter/error/default.svg`,
		type: "Error",
	};
}

interface ZebraMachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
}

const ZebraMachineErrorsAndWarnings = ({ machineId, onErrorsChanged }: ZebraMachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { data: machine } = useZebraMachine(machineId, { refetchInterval: 5000 });

	useEffect(() => {
		if (machine === undefined) {
			return;
		}

		if (machine === null) {
			onErrorsChanged(machineId, []);
			return;
		}

		if (machine.stopReason.isError === false) {
			onErrorsChanged(machine.id, []);
			return;
		}

		onErrorsChanged(machine.id, [createDisplayItem(machine.id, machine.stopReason.stopCode, intl)]);
	}, [intl, machine, machineId, onErrorsChanged]);

	return null;
};

export const ZebraErrorsAndWarnings: React.FC<ErrorsAndWarningsProps> = ({ machines, onErrorsChanged }) => {
	return (
		<>
			{machines
				.filter(({ machineType: configuredMachineType }) => configuredMachineType === "ZebraPrinter")
				.map(({ machineId }) => (
					<ZebraMachineErrorsAndWarnings key={machineId} machineId={machineId} onErrorsChanged={onErrorsChanged} />
				))}
		</>
	);
};
